import Loading from "../../../components/core/blocks/Loading";
import { useDSPAudienceDatasetsQuery } from "../hooks/useDSPAudienceDatasetsQuery";
import { useMemo, useState } from "react";
import { trellisPalette } from "../../../components/custom/analytics/palettes";
import { Col, Row } from "react-bootstrap";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { default as BasePieChart } from "../../../components/core/blocks/PieChart";
import TrellisBarChart from "../../../components/core/blocks/BarChart";

const DEFAULT_COLORS = [trellisPalette[3], trellisPalette[0], trellisPalette[2], trellisPalette[1], trellisPalette[14]];

const PieChart = ({ title, data, metric, accessor, colors = DEFAULT_COLORS }) => {
  const nameAccessor = (d) => d.segment_name.replace("Demo - ", "");
  return (
    <BasePieChart
      title={title}
      data={data}
      metric={metric}
      accessor={accessor}
      nameAccessor={nameAccessor}
      colors={colors}
    />
  );
};

const BarChart = ({
  title,
  data,
  metric,
  accessor,
  sortAccessor = null,
  topN = 5,
  hideOther = false,
  height,
  colors = DEFAULT_COLORS.slice(2),
}) => {
  const categoryAccessor = (d) =>
    d.segment_name
      .replace("Demo - ", "")
      .replace("IM - ", "")
      .replace("LS - ", "")
      .replace(title, "")
      .replace(": ", "");
  return (
    <TrellisBarChart
      title={title}
      data={data}
      metric={metric}
      accessor={accessor}
      sortAccessor={sortAccessor}
      topN={topN}
      hideOther={hideOther}
      height={height}
      colors={colors}
      categoryAccessor={categoryAccessor}
    />
  );
};

const DSPOverlapDashboard = ({ lineItemId = null, orderId = null, lineItemIds = null }) => {
  const { t } = useTranslation(["dsp"]);
  const [selectedMetric, setSelectedMetric] = useState("impressions");
  const metricOptions = [
    { value: "impressions", label: t(($) => $.dsp["Impressions"]) },
    { value: "clicks", label: t(($) => $.dsp["Clicks"]) },
    { value: "total_cost", label: t(($) => $.dsp["Cost"]) },
    { value: "total_purchases", label: t(($) => $.dsp["Total Purchases"]) },
  ];

  const { data: datasetsResult, isLoading } = useDSPAudienceDatasetsQuery({
    dimensions: ["segment_id"],
    lineItemId,
    orderId,
    lineItemIds,
  });

  const data = useMemo(() => {
    const mergedData = datasetsResult?.data ?? [];
    if (!mergedData.length) {
      return {};
    }

    const demoGroups = mergedData.filter((d) => d.segment_name.startsWith("Demo - "));
    const genderGroup = mergedData.filter((d) => ["Demo - Female", "Demo - Male"].includes(d.segment_name));
    const ageGroup = mergedData.filter((d) => d.segment_name.startsWith("Demo - Age"));
    const educationGroup = mergedData.filter((d) => d.segment_name.startsWith("Demo - Education"));
    const incomeGroup = mergedData.filter((d) => d.segment_name.startsWith("Demo - Income"));
    const occupationGroup = mergedData.filter((d) => d.segment_name.startsWith("Demo - Occupation"));
    const maritalStatusGroup = mergedData.filter((d) =>
      ["Demo - Married", "Demo - Unmarried"].includes(d.segment_name),
    );
    const lifestyleGroup = mergedData.filter((d) => d.segment_name.startsWith("LS -"));
    const inMarketGroup = mergedData.filter((d) => d.segment_name.startsWith("IM -"));
    return {
      mergedData,
      demoGroups,
      genderGroup,
      ageGroup,
      educationGroup,
      incomeGroup,
      occupationGroup,
      maritalStatusGroup,
      lifestyleGroup,
      inMarketGroup,
    };
  }, [datasetsResult]);
  const accessor = (d) => d[selectedMetric];
  const selectedMetricOption = metricOptions.find((m) => m.value === selectedMetric);
  const metric = selectedMetricOption?.label;
  const sortAccessor = (d) => d.segment_name;
  return (
    <div className="fs-stardard">
      <Row>
        <Col xs={12} lg={10}></Col>
        <Col xs={12} lg={2}>
          <Select options={metricOptions} value={selectedMetricOption} onChange={(e) => setSelectedMetric(e.value)} />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col xs={4}>
          <Row>
            <Col xs={12}>
              {data?.genderGroup && (
                <PieChart
                  accessor={accessor}
                  metric={metric}
                  title={t(($) => $.dsp["Gender"])}
                  data={data.genderGroup}
                />
              )}
            </Col>
          </Row>
          <Row className="mt-4">
            <Col xs={12}>
              {data?.maritalStatusGroup && (
                <PieChart
                  accessor={accessor}
                  metric={metric}
                  title={t(($) => $.dsp["Marital Status"])}
                  data={data.maritalStatusGroup}
                />
              )}
            </Col>
          </Row>
        </Col>
        <Col xs={8}>
          <Row>
            <Col xs={6}>
              {data?.ageGroup && (
                <BarChart
                  accessor={accessor}
                  metric={metric}
                  title={t(($) => $.dsp["Age"])}
                  data={data.ageGroup}
                  sortAccessor={sortAccessor}
                />
              )}
            </Col>
            <Col xs={6}>
              {data?.educationGroup && (
                <BarChart
                  accessor={accessor}
                  metric={metric}
                  title={t(($) => $.dsp["Education"])}
                  data={data.educationGroup}
                />
              )}
            </Col>
          </Row>
          <Row className="mt-4">
            <Col xs={6}>
              {data?.incomeGroup && (
                <BarChart
                  accessor={accessor}
                  metric={metric}
                  title={t(($) => $.dsp["Income"])}
                  data={data.incomeGroup}
                  sortAccessor={sortAccessor}
                />
              )}
            </Col>
            <Col xs={6}>
              {data?.occupationGroup && (
                <BarChart
                  accessor={accessor}
                  metric={metric}
                  title={t(($) => $.dsp["Occupation"])}
                  data={data.occupationGroup}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col xs={6}>
          {data?.lifestyleGroup && (
            <BarChart
              accessor={accessor}
              metric={metric}
              title={t(($) => $.dsp["Lifestyle Top 10"])}
              data={data.lifestyleGroup}
              topN={10}
              hideOther
              height={500}
              colors={DEFAULT_COLORS.slice(3)}
            />
          )}
        </Col>
        <Col xs={6}>
          {data?.inMarketGroup && (
            <BarChart
              accessor={accessor}
              metric={metric}
              title={t(($) => $.dsp["In Market Top 10"])}
              data={data.inMarketGroup}
              topN={10}
              hideOther
              height={500}
              colors={DEFAULT_COLORS.slice(3)}
            />
          )}
        </Col>
      </Row>
      {isLoading && <Loading height={"300px"} fullPage={false} />}
    </div>
  );
};

export default DSPOverlapDashboard;
